import React, { useEffect, useState } from 'react';

interface Expiration<T> {
  data?: T;
  expiresAt?: number;
}

function getSessionStorage<ArgType>(key: string, defaultValue: ArgType) {
  const sessionValue = sessionStorage.getItem(key);
  if (sessionValue) return JSON.parse(sessionValue);
  else if (defaultValue) return { data: defaultValue };
}

function getLocalStorage<ArgType>(key: string, defaultValue: ArgType) {
  const localValue = localStorage.getItem(key);
  if (localValue) return JSON.parse(localValue);
  else if (defaultValue) {
    return defaultValue;
  }
}

export function useLocalStorage<ArgType>(
  key: string,
  defaultValue?: ArgType,
  expirationMinutes?: number,
): [ArgType, React.Dispatch<React.SetStateAction<Expiration<ArgType>>>] {
  const [value, setValue] = useState<Expiration<ArgType>>(getLocalStorage(key, { defaultValue }));

  useEffect(() => {
    if (value) {
      let expiration = undefined;
      if (expirationMinutes) {
        expiration = value.expiresAt ? value.expiresAt : getExpirationTime(expirationMinutes);
      }
      localStorage.setItem(key, JSON.stringify({ expiresAt: expiration, data: value.data }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getValue = (): ArgType => {
    if (value && !isExpiredData(value)) return value.data ?? <ArgType>undefined;
    else {
      deleteLocalStorage(key);
    }
    return <ArgType>undefined;
  };

  return [getValue(), setValue];
}

export function useSessionStorage<ArgType>(
  key: string,
  defaultValue?: ArgType,
  expirationMinutes?: number,
): [ArgType, React.Dispatch<React.SetStateAction<Expiration<ArgType>>>] {
  const [value, setValue] = useState<Expiration<ArgType>>(getSessionStorage(key, defaultValue));

  useEffect(() => {
    if (value) {
      let expiration = undefined;
      if (expirationMinutes) {
        expiration = value.expiresAt ? value.expiresAt : getExpirationTime(expirationMinutes);
      }
      sessionStorage.setItem(key, JSON.stringify({ expiresAt: expiration, data: value.data }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getValue = (): ArgType => {
    if (value && !isExpiredData(value)) return value.data ?? <ArgType>undefined;
    else {
      deleteSessionStorage(key);
    }
    return <ArgType>undefined;
  };

  return [getValue(), setValue];
}

export function deleteSessionStorage(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch (err: any) {
    console.error(`${'Error UseSessionStorage.deleteSessionStorage: '}() >> ${err.message}`);
  }
}

export function deleteLocalStorage(key: string) {
  try {
    localStorage.removeItem(key);
  } catch (err: any) {
    console.error(`${'Error UseSessionStorage.deleteSessionStorage: '}() >> ${err.message}`);
  }
}

export function getExpirationTime(minutes: number | undefined): number | undefined {
  if (minutes) {
    let date = new Date();
    date.setMinutes(date.getMinutes() + minutes);
    return date.getTime();
  } else return undefined;
}

function isExpiredData(data: Expiration<any>): boolean {
  let expired: boolean = true;
  if (data?.expiresAt) {
    var now = new Date().getTime();
    expired = data.expiresAt < now;
  } else if (data && !data.expiresAt) {
    expired = false; //undefined expiresAt corresponds to no expiration date
  }
  return expired;
}
